import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';

interface WeeklyBreachRate {
  weekStart: string;
  weekEnd: string;
  totalMql: number;
  breached: number;
  breachRate: number;
}

interface SourceRevenue {
  channel: string;
  totalRevenue: number;
}

interface AttributionRow {
  channel: string;
  attributedRevenue: number;
}

interface MktgSalesAlignDashboard {
  slaBreachRateByWeek: WeeklyBreachRate[];
  mqlToSqlConversionPct: number;
  topSourcesByRevenue: SourceRevenue[];
  attributionSummary: AttributionRow[];
}

const CHANNEL_LABELS: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  LINKEDIN: 'LinkedIn',
  X: 'X (Twitter)',
  TIKTOK: 'TikTok',
  WEB_FORM: 'Web form',
  MANUAL: 'Manual',
  CSV_IMPORT: 'CSV import',
  REFERRAL: 'Referral',
  OTHER: 'Other',
};

function formatWeek(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Story 81.4 — Marketing-Sales Alignment dashboard.
 *
 * Four sections:
 *   1. SLA breach rate sparkline bar chart (last 12 weeks)
 *   2. MQL → SQL conversion big number
 *   3. Top sources by revenue — horizontal bars
 *   4. Attribution summary card (links to /analytics/attribution)
 *
 * Hand-rolled SVG / flexbox bars — no chart library. Consistent with
 * ChannelAnalyticsPage and the rest of the dashboards.
 */
export function MktgSalesAlignPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<MktgSalesAlignDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient
      .get<MktgSalesAlignDashboard>('/analytics/mktg-sales-align')
      .then((res) => setData(res.data))
      .catch(() => setError(t('mktgSalesAlign.error_load')))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">{t('mktgSalesAlign.loading')}</p>;
  }

  if (error || !data) {
    return <p className="p-6 text-sm text-destructive">{error || t('mktgSalesAlign.error_load')}</p>;
  }

  const maxBreachRate = Math.max(0.01, ...data.slaBreachRateByWeek.map((w) => w.breachRate));
  const maxRevenue = Math.max(1, ...data.topSourcesByRevenue.map((s) => s.totalRevenue));
  const maxAttrib = Math.max(1, ...data.attributionSummary.map((a) => a.attributedRevenue));
  const convPct = (data.mqlToSqlConversionPct * 100).toFixed(1);

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">{t('mktgSalesAlign.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('mktgSalesAlign.subtitle')}</p>
      </header>

      {/* ---- Row 1: Conversion big number ---- */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="flex flex-col gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('mktgSalesAlign.mql_sql_conversion')}
          </h2>
          <div className="text-4xl font-bold tabular-nums">{convPct}%</div>
          <p className="text-xs text-muted-foreground">
            {t('mktgSalesAlign.conversion_pct', { pct: convPct })}
          </p>
          {/* Conversion progress bar */}
          <div className="mt-2 h-3 w-full rounded-full bg-muted">
            <div
              className="h-3 rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${Math.min(100, parseFloat(convPct))}%` }}
            />
          </div>
        </Card>

        {/* ---- SLA Breach summary ---- */}
        <Card className="flex flex-col gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('mktgSalesAlign.sla_breach_rate')}
          </h2>
          {data.slaBreachRateByWeek.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('mktgSalesAlign.empty_sla')}</p>
          ) : (
            <div className="flex items-end gap-1 h-16">
              {data.slaBreachRateByWeek.map((w, i) => {
                const heightPct = maxBreachRate > 0 ? (w.breachRate / maxBreachRate) * 100 : 0;
                const isHigh = w.breachRate > 0.3;
                return (
                  <div
                    key={i}
                    className="relative flex-1 group"
                    title={`${formatWeek(w.weekStart)}: ${(w.breachRate * 100).toFixed(1)}% (${w.breached}/${w.totalMql})`}
                  >
                    <div className="flex h-16 items-end">
                      <div
                        className={`w-full rounded-t transition-all duration-300 ${
                          isHigh ? 'bg-red-400' : 'bg-primary/70'
                        }`}
                        style={{ height: `${Math.max(2, heightPct)}%` }}
                      />
                    </div>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden whitespace-nowrap rounded bg-foreground px-2 py-0.5 text-[10px] text-background group-hover:block">
                      {formatWeek(w.weekStart)}: {(w.breachRate * 100).toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {data.slaBreachRateByWeek.length > 0 && (
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>{formatWeek(data.slaBreachRateByWeek[0].weekStart)}</span>
              <span>{formatWeek(data.slaBreachRateByWeek[data.slaBreachRateByWeek.length - 1].weekStart)}</span>
            </div>
          )}
        </Card>
      </div>

      {/* ---- Row 2: Top sources by revenue ---- */}
      <Card>
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('mktgSalesAlign.top_sources')}
        </h2>
        {data.topSourcesByRevenue.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('mktgSalesAlign.empty_sources')}</p>
        ) : (
          <div className="space-y-3">
            {data.topSourcesByRevenue.map((s) => (
              <div key={s.channel} className="space-y-1">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{CHANNEL_LABELS[s.channel] ?? s.channel}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    ${s.totalRevenue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary"
                    style={{
                      width: `${(s.totalRevenue / maxRevenue) * 100}%`,
                      minWidth: s.totalRevenue > 0 ? '0.25rem' : 0,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ---- Row 3: Attribution summary (linear) ---- */}
      <Card>
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('mktgSalesAlign.attribution_summary')}
          </h2>
          <Link
            to="/analytics/attribution"
            className="text-xs font-medium text-primary hover:underline"
          >
            {t('mktgSalesAlign.see_attribution')}
          </Link>
        </div>
        {data.attributionSummary.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('mktgSalesAlign.empty_sources')}</p>
        ) : (
          <div className="space-y-3">
            {data.attributionSummary.map((a) => (
              <div key={a.channel} className="space-y-1">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{CHANNEL_LABELS[a.channel] ?? a.channel}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    ${a.attributedRevenue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-violet-500"
                    style={{
                      width: `${(a.attributedRevenue / maxAttrib) * 100}%`,
                      minWidth: a.attributedRevenue > 0 ? '0.25rem' : 0,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ---- SLA breach detail table ---- */}
      {data.slaBreachRateByWeek.some((w) => w.totalMql > 0) && (
        <Card className="overflow-hidden p-0">
          <div className="border-b bg-muted/40 px-4 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('mktgSalesAlign.sla_breach_rate')} — {t('mktgSalesAlign.week')} detail
            </h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">{t('mktgSalesAlign.week')}</th>
                <th className="px-4 py-2 text-right">{t('mktgSalesAlign.total_mql')}</th>
                <th className="px-4 py-2 text-right">{t('mktgSalesAlign.breached')}</th>
                <th className="px-4 py-2 text-right">{t('mktgSalesAlign.breach_rate')}</th>
              </tr>
            </thead>
            <tbody>
              {data.slaBreachRateByWeek
                .filter((w) => w.totalMql > 0)
                .map((w, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-2 font-mono text-xs">
                      {formatWeek(w.weekStart)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{w.totalMql}</td>
                    <td className="px-4 py-2 text-right font-mono">{w.breached}</td>
                    <td
                      className={`px-4 py-2 text-right font-mono ${
                        w.breachRate > 0.3 ? 'text-red-500 font-semibold' : ''
                      }`}
                    >
                      {(w.breachRate * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
